<template>
  <div class="sign-up-company tw:py-6 tw:px-5 tw:bg-white">
    <template v-if="isRegistrationEnabled">
      <h1 class="sign-up-company__title">Create Account</h1>
      <p class="sign-up-company__corporate-note">
        Sign up is available only for corporate email addresses.
      </p>
      <form novalidate class="sign-up-company__form" @submit.prevent="handleForm">
        <SignupCredentialsForm
          :input-data="inputData"
          :errors="errors"
          :is-email-valid="isEmailValid"
          :is-loading="isLoading"
          :is-empty-input="isEmptyInput"
          :active-input-name="activeInputName"
          email-label="Corporate Email"
          :show-referral="true"
          submit-text="Sign Up"
          :form-error="errors.default"
          @field-input="handleInput"
          @field-change="handleChange"
          @clear-field="clearInputAndErrors"
          @toggle-password="toggleShowPassword"
          @set-active-input="activeInputName = $event"
        />
      </form>
      <AuthInlineLinkPrompt action-text="Sign In" />
      <div v-if="!isBsiPocEnv" class="sign-up-company__or-divider" aria-hidden="true">
        <span class="sign-up-company__or-divider-line"></span>
        <span class="sign-up-company__or-divider-text">or</span>
        <span class="sign-up-company__or-divider-line"></span>
      </div>
      <div v-if="!isBsiPocEnv" class="sign-up-company__google-bottom">
        <AuthActionButton
          :loading="isGoogleLoading"
          full-width
          text="Sign up with Google"
          loading-text="Connecting to Google..."
          @click="handleGoogleSignup"
        />
      </div>
      <AuthTermsNotice />
      <div v-if="isLoading" class="sign-up-company__spinner">
        <Spinner />
      </div>
    </template>
    <NewPlatformNotice v-else />
  </div>
</template>

<script>
import {
  checkFormInputsForCompleteness,
  checkFormInputsForErrors,
  checkEmailValidation,
  checkPasswordLength,
  toggleShowPassword,
  validatePassword,
  validateEmail,
  hideErrors,
} from '@/services/functions';
import Spinner from '../../components/Spinner';
import AuthActionButton from '@/_core/shared/ui/AuthActionButton/AuthActionButton.vue';
import AuthInlineLinkPrompt from '@/_core/shared/ui/AuthInlineLinkPrompt/AuthInlineLinkPrompt.vue';
import SignupCredentialsForm from '@/_core/shared/ui/SignupCredentialsForm/SignupCredentialsForm.vue';
import AuthTermsNotice from '@/_core/shared/ui/AuthTermsNotice/AuthTermsNotice.vue';
import NewPlatformNotice from '@/_core/shared/ui/NewPlatformNotice/NewPlatformNotice.vue';
import { mapGetters } from 'vuex';
import { store } from '@/store';
import { setItemsToLocalStorage } from '@/services/functions';
import {
  AuthMethodEnum,
  AUTH_METHOD_STORAGE_KEY,
  REGISTRATION_ENABLED,
} from '@/utils/constants/common';

const BSI_POC_MARKER = 'indonesia';

export default {
  name: 'SignUpCompany',
  components: {
    Spinner,
    AuthActionButton,
    AuthInlineLinkPrompt,
    SignupCredentialsForm,
    AuthTermsNotice,
    NewPlatformNotice,
  },
  data() {
    return {
      inputData: {
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        referralSource: '',
      },
      utmData: {
        source: '',
        medium: '',
        campaign: '',
        term: '',
        content: '',
      },
      isLoading: false,
      isEmailValid: null,
      isEmptyInput: '',
      activeInputName: null,
      isGoogleLoading: false,
    };
  },
  computed: {
    ...mapGetters({
      errors: 'authentication/errors',
    }),
    isRegistrationEnabled() {
      return REGISTRATION_ENABLED;
    },
    isBsiPocEnv() {
      const host = (window?.location?.hostname || '').toLowerCase();
      const baseUrl = (process.env.VUE_APP_BASE_URL || '').toLowerCase();
      const authBaseUrl = (process.env.VUE_APP_AUTH_BASE_URL || '').toLowerCase();
      const contentDomain = (process.env.VUE_APP_CONTENT_DOMAIN_NAME || '').toLowerCase();

      return (
        host.includes(BSI_POC_MARKER) ||
        baseUrl.includes(BSI_POC_MARKER) ||
        authBaseUrl.includes(BSI_POC_MARKER) ||
        contentDomain.includes(BSI_POC_MARKER)
      );
    },
  },
  created() {
    this.loadStoredUtmData();
  },
  methods: {
    clearInputAndErrors(inputField) {
      if (inputField === 'email') {
        this.inputData.email = '';
        hideErrors(['emailError', 'emailValidationError']);
      } else if (inputField === 'firstName') {
        this.inputData.firstName = '';
        hideErrors(['firstNameError']);
      } else if (inputField === 'lastName') {
        this.inputData.lastName = '';
        hideErrors(['lastNameError']);
      } else if (inputField === 'referralSource') {
        this.inputData.referralSource = '';
        hideErrors(['referralSourceError']);
      }
      store.commit('authentication/clearErrors');
      document.getElementById(inputField).style.border = '1px solid #F0F0F0';
    },
    async handleForm() {
      if (!REGISTRATION_ENABLED) return;
      hideErrors([
        'passwordError',
        'passwordLengthError',
        'emailError',
        'emailValidationError',
        'firstNameError',
        'lastNameError',
        'referralSourceError',
      ]);
      store.commit('authentication/clearErrors');

      const newUserData = {
        company_name: '',
        website: '',
        address1: '',
        country: '',
        state: '',
        city: '',
        zip_code: '',
        email: this.inputData.email,
        password: this.inputData.password,
        first_name: this.inputData.firstName,
        last_name: this.inputData.lastName,
        referral_source: this.inputData.referralSource,
        type: 'company',
        accept_terms_of_use_and_privacy_policy: true,
        utm_data: this.getUtmString(),
      };

      if (this.inputData.email) {
        checkEmailValidation({ email: this.inputData.email }, 'field-input-validation-error');
      }

      if (this.inputData.password) {
        checkPasswordLength({ password: this.inputData.password }, 'field-input-length-error');
      }

      checkFormInputsForErrors(this.inputData, 'field-input-error');
      if (
        checkFormInputsForCompleteness(this.inputData) &&
        validatePassword(this.inputData.password) &&
        validateEmail(this.inputData.email)
      ) {
        await this.sendRequest(newUserData);
      }
    },
    async sendRequest(newUserData) {
      try {
        this.isLoading = true;
        await this.$auth.signUpCompany(newUserData);
        this.isLoading = false;
        if (!Object.keys(this.errors).length) {
          if (process.env.NODE_ENV === 'production') {
            gtag('event', 'sign_up', {
              method: 'oversecured',
              type: 'company',
              campaign: this.utmData.campaign,
              source: this.utmData.source,
              medium: this.utmData.medium,
              term: this.utmData.term,
              content: this.utmData.content,
            });
          }
          await this.$router.push({
            name: 'ConfirmEmailLink',
            params: { email: this.inputData.email },
          });
          if (process.env.NODE_ENV === 'production') {
            otag({
              event: 'SIGN_UP',
              email: this.inputData.email,
              utm_data: this.getUtmString(),
              utm_campaign: this.utmData.campaign,
              utm_source: this.utmData.source,
              utm_medium: this.utmData.medium,
              utm_term: this.utmData.term,
              utm_content: this.utmData.content,
            });
          }
        }
      } catch (error) {
        this.isLoading = false;
        console.log(error);
      }
    },
    handleInput(e) {
      this.checkKeyEvent(e);
      this.isEmailValid = validateEmail(this.inputData.email);

      hideErrors(['passwordError', 'passwordLengthError', 'emailError', 'emailValidationError']);
      document.getElementById('email').style.border = '1px solid #F0F0F0';
      document.getElementById('password').style.border = '1px solid #F0F0F0';
      store.commit('authentication/clearErrors');
    },
    checkKeyEvent(event) {
      this.activeInputName = event.target.id;
    },
    handleChange(e) {
      if (e.target.value.trim()) {
        const el = document.getElementById(`${e.target.id}`);
        const label = document.getElementById(`${e.target.id}Error`);
        el.classList.remove('field-input-error');
        label.style.display = 'none';
      }
    },
    loadStoredUtmData() {
      const storedUtmData = localStorage.getItem('oversecured_utm_data');

      if (storedUtmData) {
        try {
          const parsed = JSON.parse(storedUtmData);
          // Check if data is not older than 30 days
          const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
          if (parsed.timestamp && parsed.timestamp > thirtyDaysAgo) {
            this.utmData = {
              source: parsed.source || '',
              medium: parsed.medium || '',
              campaign: parsed.campaign || '',
              term: parsed.term || '',
              content: parsed.content || '',
            };
          } else {
            // Remove expired UTM data
            localStorage.removeItem('oversecured_utm_data');
          }
        } catch (error) {
          // Remove corrupted UTM data
          localStorage.removeItem('oversecured_utm_data');
        }
      }
    },
    getUtmString() {
      const parts = [];
      if (this.utmData.source) parts.push(`source=${this.utmData.source}`);
      if (this.utmData.medium) parts.push(`medium=${this.utmData.medium}`);
      if (this.utmData.campaign) parts.push(`campaign=${this.utmData.campaign}`);
      if (this.utmData.term) parts.push(`term=${this.utmData.term}`);
      if (this.utmData.content) parts.push(`content=${this.utmData.content}`);
      return parts.join('|');
    },
    handleGoogleSignup() {
      if (this.isGoogleLoading) return;
      this.isGoogleLoading = true;

      setItemsToLocalStorage([[AUTH_METHOD_STORAGE_KEY, AuthMethodEnum.SSO]]);

      const ssoUrl = `${process.env.VUE_APP_AUTH_BASE_URL}/sso/google-login`;
      window.location.href = ssoUrl;
    },
    toggleShowPassword,
  },
};
</script>

<style lang="scss">
@import '../../assets/css/fonts';

.sign-up-company {
  display: flex;
  align-items: center;
  flex-direction: column;
  position: relative;

  &__spinner {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__title {
    @include Aspekta-font(700, 60px, normal, #242424);
    font-size: calc(31px + (60 - 31) * calc((100vw - 320px) / (1920 - 320)));
    letter-spacing: 0em;
    margin: 0;
  }

  &__form {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    max-width: 395px;
    margin-top: 4px;
    margin-bottom: 12px;
  }

  &__google-bottom {
    width: 100%;
    max-width: 395px;
    display: flex;
    flex-direction: column;
  }

  &__or-divider {
    width: 100%;
    max-width: 395px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin: 30px 0;
  }

  &__or-divider-line {
    flex: 1;
    height: 1px;
    background: #d6d6d6;
  }

  &__or-divider-text {
    @include Aspekta-font(500, 12px, normal, #9c9c9c);
    letter-spacing: 0.08em;
  }

  &__corporate-note {
    width: 100%;
    max-width: 395px;
    margin: 12px 0 30px;
    @include Aspekta-font(500, 14px, 150%, #706969);
    text-align: center;
  }
}

@media (max-width: 768px) {
  .sign-up-company {
    padding: 0 20px;

    &__title {
      margin: 0;
    }
  }
}
</style>
